/** Stage E — Personal Notes panel
 * CRUD on notes attached to an entity (opportunity/analysis/proposal/position/symbol).
 * Uses simple textarea (Markdown supported by backend body; render as-is).
 */
import { useEffect, useState } from "react";
import { Loader2, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function timeSince(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString();
}

export default function NotesPanel({ entityType, entityId, symbol }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [tags, setTags] = useState("");

  const load = () => {
    if (!entityId && !symbol) return;
    setLoading(true);
    const params = symbol ? { symbol } : { entity_type: entityType, entity_id: entityId };
    api.get("/notes", { params })
      .then(r => setNotes(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [entityType, entityId, symbol]);

  const create = async () => {
    if (!draft.trim()) return;
    setCreating(true);
    try {
      const payload = {
        entity_type: entityType || "symbol",
        entity_id: entityId || (symbol || "").toUpperCase(),
        symbol: symbol ? symbol.toUpperCase() : undefined,
        body: draft.trim(),
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      };
      await api.post("/notes", payload);
      setDraft(""); setTags("");
      toast.success("Note saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save note"); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      toast.success("Note deleted");
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-3" data-testid="notes-panel">
      <div className="border border-border/40 p-2 space-y-2">
        <textarea
          data-testid="note-draft-body"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a note… (Markdown supported)"
          rows={3}
          className="w-full bg-transparent text-xs mono resize-y outline-none placeholder:text-muted-foreground"
        />
        <div className="flex items-center gap-2">
          <input
            data-testid="note-draft-tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="tags (comma-separated)"
            className="flex-1 bg-transparent text-[11px] mono outline-none placeholder:text-muted-foreground border-b border-border/40 pb-1"
          />
          <button
            onClick={create}
            disabled={creating || !draft.trim()}
            className="chip pnl-up border-current text-[10px] hover:opacity-80 disabled:opacity-40 transition-opacity"
            data-testid="note-save-btn"
          >
            {creating ? <Loader2 size={11} className="animate-spin inline mr-1"/> : <Plus size={11} className="inline mr-1"/>}
            Save
          </button>
        </div>
      </div>
      {loading && <div className="text-xs text-muted-foreground">Loading notes…</div>}
      {!loading && notes.length === 0 && (
        <div className="text-xs text-muted-foreground italic">No notes yet — add the first one above.</div>
      )}
      {notes.map(n => (
        <div key={n.id} className="border border-border/40 p-2 group" data-testid={`note-item-${n.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="chip !text-[9px] border-current text-muted-foreground">{n.entity_type}</span>
            {n.symbol && <span className="mono text-[10px]">{n.symbol}</span>}
            <span className="ml-auto mono text-[10px] text-muted-foreground">{timeSince(n.updated_at)}</span>
            <button
              onClick={() => remove(n.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-signal-red"
              data-testid={`note-delete-${n.id}`}
              aria-label="Delete note"
            >
              <Trash2 size={11}/>
            </button>
          </div>
          <div className="text-xs text-foreground/90 whitespace-pre-wrap break-words">{n.body}</div>
          {(n.tags || []).length > 0 && (
            <div className="flex items-center gap-1 flex-wrap mt-1">
              {n.tags.map((t, idx) => (
                <span key={idx} className="mono text-[9px] text-muted-foreground uppercase">#{t}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
